import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { fetchAddresses, fetchQuery } from "../../../utils/commonComponent/fetched";
import Text from "../../../utils/Custom/text";
import { AutoCompletes } from "../../../utils/Custom/custominput";
import { AddButton } from "../../../utils/Custom/Button";
import { generatePDFQuotation } from "./quotationPrint";
import DownloadPdfLoaderModal from "../../../utils/commonComponent/downloadPdfLoader";
import { getPermissions } from "../rbac/hasAccess";
import Stamp from "../../image/guidstamp.png";

const IMAGE_BASE_URL = "";
const resolveImageUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${IMAGE_BASE_URL}${path}`;
};

const DEFAULT_SIGNER = {
  name: "Komal Dushing",
  email: "komal.dushing@dycgpl.com",
  signatureUrl: null, 
};

// Bold + underlined heading, exactly like "Subject:", "Scope of Work:" in the PDF.
const SectionTitle = ({ children }) => (
  <div className="font-bold underline decoration-black mb-1 text-[14px]">{children}</div>
);

const Section = ({ title, children, className = "" }) => {
  if (!children) return null;
  return (
    <div className={`mb-4 ${className}`}>
      <SectionTitle>{title}</SectionTitle>
      <div className="whitespace-pre-line text-[14px] leading-relaxed">{children}</div>
    </div>
  );
};

export default function ViewQuotation(props) {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);

  const [inputStates, setInputStates] = React.useState({});
  const [quotationObject, updateQuotationObject] = React.useState({});
  const [productDetails, setProductDetails] = React.useState([]);
  const [letterHead, setLetterHead] = React.useState(true);
  const [downloadPdfloading, setDownloadPdfloading] = React.useState(false);
  const [signer, setSigner] = React.useState(DEFAULT_SIGNER);

  const requiredKeys = [
    "clientId", "address", "addressId", "selectedBillingAddressId",
    "contact", "contactNumber", "emailId", "contactDept", "selectedContactId",
    "contactPerson", "quotationNumber", "date", "gstType", "gstpercentages",
    "enquiryReference", "refNoEnquiry", "id", "paymentTerms", "termsConditions",
    "clientScope", "deliverables", "quotationValidity", "scopeOfThirdParty",
    "scopeInspectionLocation", "commercialsContactPerson", "inspection",
    "TechnicalContact", "professionalfees", "communicationProtocol",
    "Coordination", "inspectionservice", "subject", "scopeWork", "companyName",
    "revisionNumber",
  ];

  const fetchSignerInfo = async (createdBy) => {
    if (!createdBy) return;
    try {
      const [user] = await fetchQuery(`select * from users where id = ${createdBy}`);
      if (!user) return;

      let employee = null;
      if (user.employeeId) {
        const [emp] = await fetchQuery(`select * from employees where id = ${user.employeeId}`);
        employee = emp;
      }

      const employeeEmail = employee?.officeEmailAddress || employee?.emailAddress || "";

      setSigner({
        name: employee?.name || DEFAULT_SIGNER.name,
        email: employeeEmail || DEFAULT_SIGNER.email,
        signatureUrl: resolveImageUrl(user.usersign) || null,
      });
    } catch (error) {
      console.error("Error fetching signer info:", error);
    }
  };

  const fetchCompanyInfo = async (clientId) => {
    if (!clientId) return;
    try {
      const [client] = await fetchQuery(`select * from clients where id = ${clientId}`);
      if (client?.companyName) {
        setInputStates((prev) => ({ ...prev, companyName: client.companyName }));
      }
    } catch (error) {
      console.error("Error fetching company info:", error);
    }
  };

  const fetchquotationtDetails = async () => {
    try {
      const query = `select * from quotation where id = ${params.id}`;
      const [quotation] = await fetchQuery(query);
      if (!quotation) return;

      const filteredQuotation = Object.keys(quotation).filter((key) => requiredKeys.includes(key)).reduce((obj, key) => {
          obj[key] = quotation[key];
          return obj;
        }, {});
      //   // Resolve billing address from its id, same as the edit screen.
      // const addressId = filteredQuotation.address;
      // if (addressId) {
      //   const addresses = await fetchAddresses(addressId);
      //   filteredQuotation.address = addresses[0]?.address || null;
      // }

    // address & clientId are already plain text on the row — no lookups needed
    filteredQuotation.companyName = quotation.clientId || "";
      // Rehydrate display-only contact fields from the saved contactPerson JSON.
      if (filteredQuotation.contactPerson) {
        try {
          const parsedContact = JSON.parse(filteredQuotation.contactPerson);
          const c = Array.isArray(parsedContact) ? parsedContact[0] : parsedContact;
          filteredQuotation.contact = c?.contactPersonName || "";
          filteredQuotation.emailId = c?.emailId || "";
          filteredQuotation.contactNumber = c?.contact || "";
          filteredQuotation.contactDept = c?.dept || "";
        } catch (e) {
          console.error("Error parsing contactPerson:", e);
        }
      }

      const revNum = quotation?.revisionNumber;
      const revSuffix = revNum > 0 ? `-Rev-${revNum}` : "";
      filteredQuotation.quotationNumber = `${filteredQuotation.quotationNumber}${revSuffix}`;

      setInputStates(filteredQuotation);
      updateQuotationObject({
        discount: quotation?.discount || "",
        fileUpload: quotation?.fileUpload || "",
        revisionNumber: quotation?.revisionNumber ?? 0,
        gstType: quotation?.gstType || 1,
        GSTPercentage: quotation?.gstpercentages || 18,
      });

      // // Company name (from clientId) and signer details (from createdBy)
      // fetchCompanyInfo(quotation.clientId);
      fetchSignerInfo(quotation.createdBy);
    } catch (error) {
      console.error("Error fetching quotation:", error);
    }
  };

  useEffect(() => {
    if (params.id != 0) {
      fetchquotationtDetails();
    }
  }, [params.id]);

  const formatContactList = (str, showToCC = true) => {
    const items = (str || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (items.length === 0) return "";
    return items.map((item, idx) => showToCC ? `${idx === 0 ? "To" : "CC"}: ${item}` : item).join("\n");
  };

  // Deliverables render as a bullet list in the PDF (one bullet per line).
  const deliverableLines = (inputStates.deliverables || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const dateStr = inputStates.date ? moment(inputStates.date).format("DD/MM/YYYY") : "";
  const revNum = quotationObject?.revisionNumber ?? 0;

  return (
    <div>
      {(permissions.pdfDownload || permissions.allAccess) && (
        <div className="flex gap-10 mt-5 justify-end">
          <div className="min-w-[250px]">
            <AutoCompletes
              label="Letter Head"
              options={[
                { id: 1, label: "With Letter Head" },
                { id: 2, label: "Without Letter Head" },
              ]}
              valueKey="id"
              labelKey="label"
              value={letterHead ? 1 : 2}
              onChange={(val) => setLetterHead(val === 1)}
            />
          </div>
          <AddButton
            id="btn-print"
            label="Download PDF"
            onClick={async () => {
              await generatePDFQuotation(
                inputStates,
                letterHead,
                productDetails,
                quotationObject,
                setDownloadPdfloading,
                signer
              );
            }}
          />
        </div>
      )}

      <div className="mt-6 mx-auto max-w-[850px] bg-white shadow-lg border border-gray-200">
        <div className="px-12 py-8 text-black">
          <h1 className="text-xl font-bold mb-1">QUOTATION FOR SERVICES</h1>
          <div className="text-[14px] mb-5">
            Number: {inputStates.quotationNumber || ""}
            {dateStr ? `, Dated-${dateStr}` : ""}
          </div>

          {/* To / From box */}
          <div className="grid grid-cols-2 border border-black text-[13px] mb-6">
            <div className="border-r border-black p-3 space-y-0.5">
              <div>To,</div>
              {inputStates.contact && <div className="font-bold">{inputStates.contact}</div>}
              {inputStates.contactDept && <div>{inputStates.contactDept}</div>}
              {inputStates.companyName && <div className="font-bold">{inputStates.companyName}</div>}
              {inputStates.address && <div className="whitespace-pre-line">{inputStates.address}</div>}
              {inputStates.contactNumber && <div>Mobile No.: {inputStates.contactNumber}</div>}
              {inputStates.emailId && <div>Email ID: {inputStates.emailId}</div>}
            </div>
            <div className="p-3 space-y-0.5">
              <div>From</div>
              <div className="font-bold">{signer.name}</div>
              <div className="font-bold">DYC Global Private Limited</div>
              <div>B-702, Lodha Supremus, Kolshet,</div>
              <div>Thane (West), Thane – 400607,</div>
              <div>Maharashtra, India.</div>
              <div>Mobile No.: +91 9284011025</div>
              <div>Landline No.: +91 2249628259</div>
              <div>Email Id.: {signer.email}</div>
            </div>
          </div>

          <Section title="Subject:">{inputStates.subject}</Section>
          <Section title="References:">{inputStates.enquiryReference}</Section>

          <p className="text-[14px] mb-3">Dear Sir,</p>
          <p className="text-[14px] mb-3">
            We acknowledge receipt of your aforementioned enquiry with gratitude. Your
            expressed interest in the services offered by DYC Global Private Limited is
            greatly appreciated.
          </p>
          <p className="text-[14px] mb-4">
            We are delighted to present our proposal as outlined below:
          </p>

          <Section title="Scope of Work:">{inputStates.scopeWork}</Section>

          <Section title="Deliverable:">{inputStates.deliverables}</Section>

          <p className="text-[14px] font-bold mb-4">
            The Management/Project Team of the client shall inform the Coordinator at DYC
            Global Private Limited at least 3 working days in advance regarding the
            inspection activities to be carried out at the vendor location.
          </p>

          <Section title="Communication Protocol:">{inputStates.communicationProtocol}</Section>
          <Section title="Coordination:">{inputStates.Coordination}</Section>

          <div className="mb-1">
            <SectionTitle>Inspection Services:</SectionTitle>
          </div>

          {formatContactList(inputStates.inspection) && (
            <div className="mb-3 pl-4">
              <div className="font-bold text-[14px]">Inspection:</div>
              <div className="whitespace-pre-line text-[14px] pl-4">
                {formatContactList(inputStates.inspection)}
              </div>
            </div>
          )}

          {formatContactList(inputStates.commercialsContactPerson) && (
            <div className="mb-3 pl-4">
              <div className="font-bold text-[14px]">Commercials:</div>
              <div className="whitespace-pre-line text-[14px] pl-4">
                {formatContactList(inputStates.commercialsContactPerson)}
              </div>
            </div>
          )}

          {formatContactList(inputStates.TechnicalContact, false) && (
            <div className="mb-3 pl-4">
              <div className="font-bold text-[14px]">Technical:</div>
              <div className="whitespace-pre-line text-[14px] pl-4">
                {formatContactList(inputStates.TechnicalContact, false)}
              </div>
            </div>
          )}

          <Section title="Professional Fees:">{inputStates.professionalfees}</Section>
          <Section title="Payment Terms:">{inputStates.paymentTerms}</Section>
          <Section title="Validity:">{inputStates.quotationValidity}</Section>
          <Section title="Terms & Conditions:">{inputStates.termsConditions}</Section>

          <p className="text-[14px] mt-6 mb-1">Sincerely Yours,</p>
          <p className="text-[14px] font-bold mb-2">For DYC Global Private Limited</p>

          {signer.signatureUrl ? (
            <img src={signer.signatureUrl} alt="signature" className="h-16 mb-2 object-contain" />
          ) : <div className="h-16"></div>}
          <p className="text-[14px] font-bold mb-6">{signer.name}</p>

          <Section title="Attachment(s):">
            {`DYC General terms & Conditions - DYC/FMT-MR/29 Rev. 00 Revision date: 24.05.2023
Revision Number [${String(revNum).padStart(2, "0")}]: -
Reason for Revision: -
This Document Supersedes Quotation No.: -`}
          </Section>

          <div className="border-t border-gray-300 my-8" />

          <Section title="Acceptance of Quotation:">
            {`I/We confirm the acceptance of DYC Global's Quotation No. ${inputStates.quotationNumber || ""
              }${dateStr ? ` Dated ${dateStr}` : ""}`}
          </Section>

          <p className="text-[14px] mt-8">
            Client signature &amp; Company's seal_____________________________
          </p>
          <p className="text-[14px] mt-8">Date ________________</p>
          <p className="text-[14px] mt-8">Name / Designation: _________________________</p>
        </div>
      </div>

      {downloadPdfloading && ( <DownloadPdfLoaderModal loading={downloadPdfloading} name={"Report"} />)}
    </div>
  );
}